// /src/pages/PrivacyPolicy.js
// Public page — no auth required
// Route: /privacy-policy

export default function PrivacyPolicy() {
  const lastUpdated = "March 22, 2026";

  const sections = [
    {
      title: "1. Information We Collect",
      content: `We collect information you provide directly to us when you submit a lead form, contact us, or interact with our services. This includes:

• Name, email address, and phone number
• Financial information such as approximate credit score range
• Business information including LLC status and company name
• Property and project details related to real estate financing inquiries
• Communication preferences and responses to qualification questions

We also collect information automatically when you interact with our digital platforms, including IP address, browser type, pages visited, and timestamps.`,
    },
    {
      title: "2. How We Use Your Information",
      content: `We use the information we collect to:

• Process and respond to your financing inquiries
• Determine your eligibility for our loan products
• Contact you via email, SMS, or phone regarding your application
• Send automated follow-up communications related to your inquiry
• Schedule calls or meetings with our loan specialists
• Improve our services and marketing efforts
• Comply with legal and regulatory obligations

We use automated systems, including AI-assisted tools, to help qualify leads and respond to initial inquiries. You may always request to speak with a human representative.`,
    },
    {
      title: "3. Facebook and Meta Platforms",
      content: `We use Facebook Lead Ads and Facebook Messenger to collect inquiries and communicate with potential clients. When you interact with our Facebook Page or ads:

• Facebook may share your name, email, phone number, and other form responses with us
• We may use Facebook Messenger to send you automated responses and follow-up messages
• Your interactions are subject to both this Privacy Policy and Meta's Data Policy
• We do not sell your data to third parties for advertising purposes

To learn more about how Meta handles your data, visit: https://www.facebook.com/privacy/policy/`,
    },
    {
      title: "4. How We Share Your Information",
      content: `We do not sell your personal information. We may share your information with:

• Our loan specialists and internal team members for the purpose of processing your inquiry
• Third-party service providers who assist with email delivery, CRM management, and communication tools (including Resend, Cloudflare, and similar infrastructure providers)
• Legal authorities when required by law or to protect our rights
• Business successors in the event of a merger, acquisition, or sale of assets

All third-party providers are contractually obligated to protect your information and use it only for the services they provide to us.`,
    },
    {
      title: "5. Data Retention",
      content: `We retain your personal information for as long as necessary to fulfill the purposes described in this policy, including:

• Active leads: retained for the duration of your financing inquiry and any resulting loan
• Inactive leads: retained for up to 3 years for re-engagement and compliance purposes
• Communication records: retained for up to 7 years as required by financial regulations

You may request deletion of your data at any time by contacting us at support@propertunitycapital.com.`,
    },
    {
      title: "6. Your Rights and Choices",
      content: `Depending on your location, you may have the following rights:

• Access: Request a copy of the personal information we hold about you
• Correction: Request correction of inaccurate or incomplete information
• Deletion: Request deletion of your personal information
• Opt-out: Unsubscribe from marketing communications at any time
• Portability: Request your data in a portable format

To exercise any of these rights, contact us at support@propertunitycapital.com. We will respond within 30 days.`,
    },
    {
      title: "7. Security",
      content: `We implement industry-standard security measures to protect your personal information, including encrypted data storage, secure data transmission (HTTPS), and access controls limiting who can view your information.

However, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security of your data and encourage you to contact us immediately if you suspect any unauthorized access.`,
    },
    {
      title: "8. Children's Privacy",
      content: `Our services are intended for adults aged 18 and older. We do not knowingly collect personal information from individuals under 18. If we become aware that we have collected information from a minor, we will delete it promptly. If you believe we have inadvertently collected such information, please contact us immediately.`,
    },
    {
      title: "9. Changes to This Policy",
      content: `We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of material changes by updating the "Last Updated" date at the top of this page. Your continued use of our services after any changes constitutes your acceptance of the updated policy.`,
    },
    {
      title: "10. Contact Us",
      content: `If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:

Propertunity Capital
Email: support@propertunitycapital.com
Website: https://propertunity-crm.pages.dev

We are committed to resolving any privacy concerns promptly and transparently.`,
    },
  ];

  return (
    <div style={{
      minHeight: "100vh",
      background: "#f8fafc",
      fontFamily: "'Georgia', serif",
    }}>
      {/* Header */}
      <div style={{
        background: "#0f172a",
        padding: "48px 24px",
        textAlign: "center",
      }}>
        <div style={{
          fontSize: 13,
          letterSpacing: "0.15em",
          color: "#60a5fa",
          textTransform: "uppercase",
          marginBottom: 16,
          fontFamily: "'Arial', sans-serif",
          fontWeight: 600,
        }}>
          Propertunity Capital
        </div>
        <h1 style={{
          fontSize: "clamp(28px, 5vw, 48px)",
          fontWeight: 400,
          color: "#f1f5f9",
          margin: "0 0 16px",
          letterSpacing: "-0.02em",
          lineHeight: 1.2,
        }}>
          Privacy Policy
        </h1>
        <p style={{
          color: "#94a3b8",
          fontSize: 14,
          fontFamily: "'Arial', sans-serif",
          margin: 0,
        }}>
          Last updated: {lastUpdated}
        </p>
      </div>

      {/* Intro */}
      <div style={{
        maxWidth: 760,
        margin: "0 auto",
        padding: "48px 24px 0",
      }}>
        <div style={{
          background: "#fff",
          borderRadius: 12,
          padding: "32px 40px",
          borderLeft: "4px solid #1d4ed8",
          marginBottom: 32,
          boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        }}>
          <p style={{
            fontSize: 16,
            lineHeight: 1.8,
            color: "#334155",
            margin: 0,
            fontStyle: "italic",
          }}>
            Propertunity Capital ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you interact with our services, website, or Facebook pages and advertisements.
          </p>
        </div>

        {/* Sections */}
        {sections.map((section, i) => (
          <div key={i} style={{
            background: "#fff",
            borderRadius: 12,
            padding: "32px 40px",
            marginBottom: 16,
            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
          }}>
            <h2 style={{
              fontSize: 18,
              fontWeight: 700,
              color: "#0f172a",
              margin: "0 0 20px",
              fontFamily: "'Arial', sans-serif",
              letterSpacing: "-0.01em",
            }}>
              {section.title}
            </h2>
            <div style={{
              fontSize: 15,
              lineHeight: 1.85,
              color: "#475569",
              whiteSpace: "pre-line",
              fontFamily: "'Georgia', serif",
            }}>
              {section.content}
            </div>
          </div>
        ))}

        {/* Footer */}
        <div style={{
          textAlign: "center",
          padding: "40px 24px 64px",
          color: "#94a3b8",
          fontSize: 13,
          fontFamily: "'Arial', sans-serif",
        }}>
          © {new Date().getFullYear()} Propertunity Capital. All rights reserved.
          <br />
          <a href="mailto:support@propertunitycapital.com" style={{ color: "#1d4ed8", textDecoration: "none", marginTop: 8, display: "inline-block" }}>
            support@propertunitycapital.com
          </a>
        </div>
      </div>
    </div>
  );
}